<template>
  <div v-if="elementId">
    <div :id="mapContainer" class="map-container"></div>
  </div>
</template>

<script setup>
import { nextTick } from 'vue'
import mapBox from 'mapbox-gl'

/**
 * This component encapsulates the rendering of a map for a given search result.
 * elementId - A unique ID unique to the searchResult that will determine the ID on the container the map is placed in.
 * latitude - The latitude of the location.
 * longitude - The longitude of the location.
 * popupText - A function that will determine the text in the popup for marker.
 */

  const props = defineProps({
    elementId: {
      type: String,
      required: true,
    },
    latitude: {
      type: Number,
      required: false,
    },
    longitude: {
      type: Number,
      requird: false,
    },
    zoom: {
      type: Number,
      required: false,
      default: 8,
    },
    popupText: {
      type: Function,
      required: false,
    },
  })

  const emit = defineEmits(['coordinates'])

  const { elementId } = props
  const map = ref('')
  const marker = ref('')
  const { mapboxToken } = useFCPModel()
  const mapContainer = computed(() => `map-container-${elementId}`)
  const latitude = computed(() => props.latitude)

  //Watch the change in latitude.  If the map has been created, then add a marker for the new lat,long. Otherwise, create the
  //map with the new lat,long.
  watch(latitude, () => {
    if (map.value) addMarker(props.latitude, props.longitude)
    else drawMap()
  })

  //Draw the map.
  const drawMap = () => {
    mapBox.accessToken = mapboxToken.value

    map.value = new mapBox.Map({
      container: `${mapContainer.value}`,
      style: 'mapbox://styles/mapbox/streets-v11',
      //style: 'mapbox://styles/mapbox/navigation-day-v1',
      center: props.latitude && props.longitude ? [props.longitude, props.latitude] : [-93.265, 44.9778],
      zoom: props.zoom,
    })

    map.value.scrollZoom.disable()
    map.value.addControl(new mapBox.NavigationControl())

    if (props.latitude && props.longitude) addMarker(props.latitude, props.longitude)

    // ** NOTE ** This code facilitates the ability to notifiy the parent of a click on a different lat/long location
    // on the map.  The Coveo POC, for example, uses this functionality to re-execute its query when the user clicks on 
    // a different place on the map.  However, this seems to get in the way of allowing the user to click on the marker
    // popup.  So, for now, we'll comment this out.
    //
    // map.value.on('click', (e) => {
    //   console.log('clicked', e)
    //   addMarker(e.lngLat.lat, e.lngLat.lng)
    //   console.log("LATLONG CHANGE", e)
    //   emit('coordinates', e.lngLat)
    // })
  }

  const addMarker = (latitude, longitude) => {
    if (latitude && longitude) {
      if (marker.value) {
        marker.value.remove()
        marker.value = ''
      }

      const lngLat = new mapBox.LngLat(longitude, latitude)
      marker.value = new mapBox.Marker({ color: 'red', scale: '.8' }).setLngLat(lngLat)
      if (props.popupText) {
        marker.value.setPopup(new mapBox.Popup().setHTML(props.popupText()))
        marker.value.on('click', () => {
          //console.log("Marker clicked!!")
          marker.value.togglePopup()
        })
      }
      marker.value.addTo(map.value)
      map.value.setCenter(lngLat)
    }
  }

  onMounted(() => {
    setTimeout(() => {
      drawMap()
    },1400)
  })

  onUnmounted(() => {
    map.value.remove()
  })

</script>
<style scoped>
.map-container {
  width: 100%;
  height: 250px;
}

.mapboxgl-popup-content {
  line-height: var(--text-body-line-height-xs);
}
</style>
